import { Grid, Typography, Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { BarChart } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  MainStatsCards,
  UserActivityCard,
  TopPerformersCard,
  TaskInsightsCard,
  GrowthMetricsCard,
} from "./stats";
import useStatsCalculations from "./stats/useStatsCalculations";

dayjs.extend(relativeTime);

const GlobalStats = () => {
  const { analytics, users, loading } = useSelector((state) => state.admin);
  const stats = useStatsCalculations(analytics, users);

  if (loading?.stats) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress size={64} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <BarChart sx={{ fontSize: 32, color: "primary.main" }} />
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          Dashboard Overview
        </Typography>
      </Box>

      {/* Main Stats Cards */}
      <MainStatsCards
        nonAdminUserCount={stats.nonAdminUserCount}
        newUsersThisWeek={stats.newUsersThisWeek}
        newUsersToday={stats.newUsersToday}
        newUsersThisMonth={stats.newUsersThisMonth}
        nonAdminTotalVisits={stats.nonAdminTotalVisits}
        avgVisitsPerUser={stats.avgVisitsPerUser}
        visitsToday={stats.visitsToday}
        visitsThisWeek={stats.visitsThisWeek}
        visitsThisMonth={stats.visitsThisMonth}
        nonAdminTasksCreated={stats.nonAdminTasksCreated}
        avgTasksPerUser={stats.avgTasksPerUser}
        tasksCreatedThisWeek={stats.tasksCreatedThisWeek}
        tasksCreatedToday={stats.tasksCreatedToday}
        tasksCreatedThisMonth={stats.tasksCreatedThisMonth}
        subscribedUsers={stats.subscribedUsers}
        monthlyRevenue={stats.monthlyRevenue}
      />

      {/* Activity & Insights */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <GrowthMetricsCard
            newUsersThisWeek={stats.newUsersThisWeek}
            newUsersToday={stats.newUsersToday}
            newUsersThisMonth={stats.newUsersThisMonth}
            tasksCreatedThisWeek={stats.tasksCreatedThisWeek}
            tasksCreatedToday={stats.tasksCreatedToday}
            tasksCreatedThisMonth={stats.tasksCreatedThisMonth}
            avgVisitsPerUser={stats.avgVisitsPerUser}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TopPerformersCard topUsers={stats.topUsers} />
        </Grid>
      </Grid>

      {/* Additional Insights */}
      <Grid
        container
        spacing={3}
        sx={{
          "& > .MuiGrid-item": {
            mb: { xs: 2, sm: 3, md: 0 },
          },
        }}
      >
        <Grid item xs={12} md={6}>
          <TaskInsightsCard
            deletionRate={stats.deletionRate}
            tasksDeleted={stats.tasksDeleted}
            tasksCreated={stats.tasksCreated}
            nonAdminTasksCreated={stats.nonAdminTasksCreated}
            nonAdminTasksCompleted={stats.nonAdminTasksCompleted}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <UserActivityCard
            activeUsers={stats.activeUsers}
            nonAdminUserCount={stats.nonAdminUserCount}
            activityStatus={stats.activityStatus}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default GlobalStats;
